import { toast } from 'sonner';
import api from '@/lib/api';
import { formatApiError } from '@/lib/formatApiError';
import { isDesktopApp } from '@/services/config/env';

export function syncUserCredits(balance, account = null) {
  try {
    const prev = JSON.parse(localStorage.getItem('storycraft_user') || '{}');
    const next = { ...prev, credit_balance: balance };
    if (account && typeof account === 'object') {
      if (account.current_plan_id) next.current_plan_id = account.current_plan_id;
      if (account.current_plan_name) next.current_plan_name = account.current_plan_name;
      if (typeof account.paid === 'boolean') next.account_paid = account.paid;
      if (typeof account.advanced_analytics === 'boolean') {
        next.advanced_analytics = account.advanced_analytics;
      }
    }
    localStorage.setItem('storycraft_user', JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

export const RAZORPAY_CHECKOUT_STORAGE_KEY = 'twinnee_razorpay_checkout';
export const RAZORPAY_MESSAGE_SUCCESS = 'twinnee:razorpay-success';
export const RAZORPAY_MESSAGE_FAILED = 'twinnee:razorpay-failed';
export const RAZORPAY_MESSAGE_CANCELLED = 'twinnee:razorpay-cancelled';

const CHECKOUT_TTL_MS = 20 * 60 * 1000;

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(window.Razorpay);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error('Failed to load Razorpay checkout'));
    document.body.appendChild(script);
  });
}

export function billingCheckoutUrl() {
  const path = '/billing/checkout';
  if (isDesktopApp()) {
    const base = `${window.location.origin}${window.location.pathname}`;
    return `${base}#${path}`;
  }
  return `${window.location.origin}${path}`;
}

export function stashCheckoutSession(order, extra = {}) {
  const payload = {
    ...order,
    ...extra,
    saved_at: Date.now(),
  };
  localStorage.setItem(RAZORPAY_CHECKOUT_STORAGE_KEY, JSON.stringify(payload));
  return payload;
}

export function readCheckoutSession() {
  try {
    const raw = localStorage.getItem(RAZORPAY_CHECKOUT_STORAGE_KEY);
    if (!raw) return null;
    const payload = JSON.parse(raw);
    if (payload.saved_at && Date.now() - payload.saved_at > CHECKOUT_TTL_MS) {
      clearCheckoutSession();
      return null;
    }
    return payload;
  } catch {
    return null;
  }
}

export function clearCheckoutSession() {
  localStorage.removeItem(RAZORPAY_CHECKOUT_STORAGE_KEY);
}

function normalizeContactPhone(phone) {
  if (!phone || typeof phone !== 'string') return '';
  const trimmed = phone.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('+')) return trimmed;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) return `+91${digits}`;
  if (digits.length > 0) return `+${digits}`;
  return '';
}

function buildRazorpayPrefill(user = {}) {
  return {
    email: user.email || '',
    name: user.name || '',
    contact: normalizeContactPhone(user.phone || user.contact || ''),
  };
}

function notifyOpener(message) {
  if (window.opener && !window.opener.closed) {
    try {
      window.opener.postMessage(message, window.location.origin);
    } catch {
      /* ignore */
    }
  }
}

function buildRazorpayOptions(session, { finish }) {
  const user = JSON.parse(localStorage.getItem('storycraft_user') || '{}');

  return {
    key: session.key_id,
    amount: session.amount_paise,
    currency: 'INR',
    name: 'Twinnee',
    description: `Recharge ${session.credits?.toFixed?.(0) ?? session.credits} credits`,
    order_id: session.order_id,
    handler: async (response) => {
      try {
        const { data: verified } = await api.post('/billing/recharge/verify', {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });
        clearCheckoutSession();
        if (typeof verified.balance === 'number') {
          syncUserCredits(verified.balance, verified);
        }
        notifyOpener({
          type: RAZORPAY_MESSAGE_SUCCESS,
          verified,
        });
        finish({ ok: true, verified });
      } catch (err) {
        toast.error(formatApiError(err, 'Payment verification failed'));
        notifyOpener({ type: RAZORPAY_MESSAGE_FAILED });
        finish({ ok: false, error: err });
      }
    },
    prefill: buildRazorpayPrefill(user),
    theme: { color: '#6366F1' },
    modal: {
      backdropclose: false,
      escape: true,
      ondismiss: () => {
        notifyOpener({ type: RAZORPAY_MESSAGE_CANCELLED });
        finish({ ok: false, cancelled: true });
      },
    },
  };
}

export async function runRazorpayCheckout(session, { onSettled } = {}) {
  const Razorpay = await loadRazorpayScript();

  return new Promise((resolve) => {
    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      onSettled?.();
      resolve(result);
    };

    const options = buildRazorpayOptions(session, { finish });
    const rzp = new Razorpay(options);
    rzp.on('payment.failed', (resp) => {
      toast.error(resp.error?.description || 'Payment failed');
      notifyOpener({ type: RAZORPAY_MESSAGE_FAILED });
      finish({ ok: false, error: resp.error });
    });
    rzp.open();
  });
}

/** Open checkout tab synchronously on user click (before async order create). */
export function prepareRazorpayCheckoutTab() {
  const tab = window.open('', '_blank');
  if (!tab) return null;

  try {
    tab.document.title = 'Twinnee checkout';
    tab.document.body.innerHTML =
      '<div style="font-family:system-ui,sans-serif;padding:2rem;text-align:center;color:#334155">Preparing Razorpay checkout…</div>';
  } catch {
    /* ignore cross-origin write failures */
  }

  return tab;
}

export function navigateRazorpayCheckoutTab(tab, order, extra = {}) {
  if (!tab || tab.closed) return false;
  stashCheckoutSession(order, extra);
  tab.location.href = billingCheckoutUrl();
  return true;
}

/** @deprecated Use prepareRazorpayCheckoutTab + navigateRazorpayCheckoutTab */
export function openRazorpayCheckoutTab(order, extra = {}) {
  const tab = prepareRazorpayCheckoutTab();
  if (!tab) {
    toast.error('Allow pop-ups to open Razorpay in a new tab');
    return null;
  }
  navigateRazorpayCheckoutTab(tab, order, extra);
  return tab;
}

export function subscribeRazorpayMessages({ onSuccess, onFailed, onCancelled } = {}) {
  const handler = (event) => {
    if (event.origin !== window.location.origin) return;
    const { type, verified } = event.data || {};
    if (type === RAZORPAY_MESSAGE_SUCCESS) {
      onSuccess?.(verified);
    } else if (type === RAZORPAY_MESSAGE_FAILED) {
      onFailed?.();
    } else if (type === RAZORPAY_MESSAGE_CANCELLED) {
      onCancelled?.();
    }
  };
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}
